import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';

import ErgDetailModel from '../models/ERG/ErgDetail';
import POVBlock from '../models/POVBlock/POVBlock';

import JsonLd from '../components/jsonld.jsx';
import Layout from '../components/layout';
import Video from '../components/video.jsx';
import SEO from '../components/seo';
import renderHeroSection from '../components/about-us/hero-section.jsx';
import QuoteCarousel from '../components/quote-carousel.jsx';
import RelatedInsights from '../components/related-insights.jsx';
import FooterCTA from '../components/contact-us/footer-cta.jsx';
import PointOfViewSection from '../components/point-of-view-section.jsx';
import ImageWithTitleset from '../components/about-us/image-with-titleset.jsx';
import FeaturedContentCard from '../components/featured-content-card.jsx';
import AwardCarousel from '../components/award-carousel.jsx';
import ExpertsSection from '../components/experts.jsx';

import { Paper, Container, TitleSet } from '../ui-components';

import { renderFeaturedQuoteSection } from './careers/employee-resource-groups.jsx';

import CONTENT from '../static-content/employee-resource-groups.json';

import renderRichText from '../utils/render-rich-text.jsx';

/**
 * Component that renders an ERG's page content
 *
 * @component ErgDetail
 *
 * @param {object} props
 *
 * @prop {object} data
 * @prop {object} pageContext
 *
 * @returns {JSX}
 */

const ErgDetail = props => {
  const { data, pageContext } = props;
  const globalErgReference = data?.contentfulErgDetails?.localizationSlug === data?.globalErgReference?.slug && data?.globalErgReference;
  const ergDetailModel = new ErgDetailModel(data.contentfulErgDetails, globalErgReference);
  const {
    ergTitle,
    ergBody,
    ergLabel,
    ergHeroImages,
    quoteSlider,
    ourObjectivesBody,
    ourObjectivesTitle,
    ourObjectivesTiles,
    whyItMatters,
    featuredContent,
    seeTheDifferenceLabel,
    seeTheDifferenceTitle,
    seeTheDifferenceBody,
    seeTheDifferenceVideo,
    editorialSection,
    featuredQuote,
    awardsLabel,
    awardsTitle,
    awardsBody,
    awardsIcons,
    relatedInsights,
    expertsLabel,
    expertsTitle,
    expertsBody,
    footerCta
  } = ergDetailModel;
  const ourObjectivesTrue = ourObjectivesBody && ourObjectivesTitle && ourObjectivesTiles;
  const seeTheDifferenceTrue =
    seeTheDifferenceLabel && seeTheDifferenceTitle && seeTheDifferenceBody && seeTheDifferenceVideo;

  /**
   * Renders the SEO metatag and structured data elements
   *
   * @function renderSEO
   *
   * @returns {JSX}
   */
  const renderSEO = () => {
    return (
      <>
        <SEO
          title={ergDetailModel.ergTitle}
          description={ergDetailModel.snippet}
          slug={`${ergDetailModel.slug}`}
          image={
            ergDetailModel?.ergHeroImages?.length &&
            ergDetailModel.ergHeroImages[0]?.gatsbyImageData?.images?.fallback?.src
          }
        />
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'Article',
            name: ergDetailModel.ergTitle,
            alternateName: ergDetailModel.ergTitle,
            description: ergDetailModel.snippet,
            abstract: ergDetailModel.snippet,
            headline: ergDetailModel.snippet,
            alternativeHeadline: ergDetailModel.snippet,
            inLanguage: 'en',
            url: `http://credera.com/${ergDetailModel.slug}`,
            isAccessibleForFree: true,
            image:
              ergDetailModel?.ergHeroImages?.length &&
              ergDetailModel.ergHeroImages[0]?.gatsbyImageData?.images?.fallback?.src
          }}
        />
      </>
    );
  };

  const renderCustomSectionOne = () => {
    if ((ourObjectivesTitle, ourObjectivesBody, ourObjectivesTiles)) {
      const povBlock = {
        pointOfViewTitle: ourObjectivesTitle,
        pointOfViewLabel: 'Our Approach',
        pointOfViewBody: ourObjectivesBody,
        pointOfViewItems: ourObjectivesTiles
      };
      return (
        <>
          <PointOfViewSection pointOfView={new POVBlock(povBlock)} geoSpace={pageContext.space} />
        </>
      );
    } else if (whyItMatters && featuredContent) {
      return (
        <>
          <PointOfViewSection
            pointOfView={whyItMatters}
            geoSpace={pageContext.space}
            featuredContent={featuredContent}
          />
          {/* TODO 19966: To allow featuredContent to be a Case Study, update the featuredContent field query in the erg fragment to include an on ContentfulCaseStudy case */}
          <FeaturedContentCard featuredContentRaw={featuredContent} />
        </>
      );
    } else if (seeTheDifferenceLabel && seeTheDifferenceTitle && seeTheDifferenceBody && seeTheDifferenceVideo) {
      return (
        <Container>
          <TitleSet
            label={seeTheDifferenceLabel}
            title={seeTheDifferenceTitle}
            bodyText={renderRichText(seeTheDifferenceBody)}
          />
          <Video embedUrl={seeTheDifferenceVideo.embedUrl} title={seeTheDifferenceVideo.title} margin={'40px auto'} />
        </Container>
      );
    }
  };

  const renderCustomSectionTwo = () => {
    if (featuredQuote && featuredQuote.quote && featuredQuote.author) {
      return <FeaturedQuoteSectionWrapper>{renderFeaturedQuoteSection(featuredQuote)}</FeaturedQuoteSectionWrapper>;
    } else if (awardsLabel && awardsTitle && awardsBody && awardsIcons) {
      return (
        <StyledContainer sectionContainer>
          <TitleSet label={awardsLabel} title={awardsTitle} bodyText={renderRichText(awardsBody)} />
          {<AwardCarousel awards={awardsIcons}></AwardCarousel>}
        </StyledContainer>
      );
    }
  };

  const renderCustomSectionThree = () => {
    if (relatedInsights) {
      return <RelatedInsights insights={relatedInsights} />;
    } else {
      const refinedExperts = [];
      data?.experts?.edges.forEach(expert => {
        expert.node.expertise?.forEach(field => {
          if (field?.internal?.type === 'ContentfulErgDetails' && field?.ergTitle === ergTitle) {
            refinedExperts.push(expert.node);
          }
        });
      });

      if (refinedExperts.length) {
        return (
          <ExpertsSection
            expertsLabel={expertsLabel ? expertsLabel : CONTENT.EXPERTS__LABEL}
            expertsTitle={expertsTitle ? expertsTitle : CONTENT.EXPERTS__TITLE}
            expertsContent={expertsBody ? expertsBody : CONTENT.EXPERTS__CONTENT}
            experts={refinedExperts}
          />
        );
      }
    }
  };

  const renderEditorialSection = () => {
    return editorialSection?.map((section, index) => {
      return (
        <ImageWithTitleset
          key={index}
          imageLeft={index % 2 == 0}
          contentBlock={section}
          editorial
          altMobileOrder={index % 2 == 0}
        />
      );
    });
  };

  return (
    <Layout>
      {renderSEO()}
      <Paper bottom>
        {renderHeroSection(ergTitle, ergBody, ergLabel, ergHeroImages, 'detail')}
        {quoteSlider && <QuoteCarousel quotes={quoteSlider} />}
      </Paper>
      {renderCustomSectionOne()}
      <Paper
        top
        featuredContentCardTop={!ourObjectivesTrue && whyItMatters && featuredContent && !seeTheDifferenceTrue}
      >
        {editorialSection && renderEditorialSection()}
      </Paper>
      <Paper bottom subtle></Paper>
      {renderCustomSectionTwo()}
      <Paper top subtle>
        {renderCustomSectionThree()}
      </Paper>
      <FooterCTA footerCta={footerCta} pageGeo={pageContext.space} />
    </Layout>
  );
};

const FeaturedQuoteSectionWrapper = styled.div`
  #quote-container {
    padding-bottom: 0px;
  }
`;

const StyledContainer = styled(props => <Container {...props} />)`
  padding-bottom: 0;
`;

export default ErgDetail;

export const pageQuery = graphql`
  query ErgBySlug($slug: String!, $spaceId: String!, $globalSpaceId: String!, $globalErgId: String) {
    contentfulErgDetails(slug: { eq: $slug }, spaceId: { eq: $spaceId }) {
      ...CompleteErg
    }
    globalErgReference: contentfulErgDetails(spaceId: { eq: $globalSpaceId }, contentful_id: { eq: $globalErgId }) {
      ...CompleteErg
    }
    experts: allContentfulPerson(filter: { spaceId: { eq: $globalSpaceId }, expert: { eq: true } }) {
      edges {
        node {
          ...CompletePerson
        }
      }
    }
  }
`;
